/**
 * Customer-facing ZIP messaging — jobsite service eligibility only.
 * Distance, zones, and delivery fees stay in blissDeliveryDistance + quote engine.
 *
 * Geography: Full Service (build + install) east of Orange Beach, AL meridian.
 * West of that line: quoted Design Only service and $149 room consultation only.
 */

import { getZipCoordinates } from './blissDeliveryDistance'

export type BlissZipServiceStatus =
  | 'incomplete'
  | 'invalid'
  | 'full_service'
  | 'design_consult_only'

/** Orange Beach, AL — western boundary for Full Service fabrication. */
export const ORANGE_BEACH_AL_MERIDIAN_LNG = -87.5731

/** Shown in the funnel, PDF, and email — never mention distance or pricing from ZIP. */
export const BLISS_ZIP_CUSTOMER_COPY = {
  funnelSubtitle: 'Where the bunks will be built and installed.',
  funnelHint:
    'Use the Jobsite address Zip Code, not your billing ZIP. Full Service is available east of Orange Beach, Alabama.',
  fullService:
    'This jobsite ZIP qualifies for Full Service — Bliss Bedrooms can design, build, deliver, and install.',
  designConsultOnly:
    'Full Service isn’t available at this jobsite ZIP (west of Orange Beach, Alabama). You can still reserve a quoted Design Only service or $149 room consultation.',
  invalidZip: 'Enter a valid 5-digit US jobsite ZIP code.',
  funnelBlocked: 'Enter a valid 5-digit jobsite ZIP code to continue.',
} as const

const INVALID_ZIP_PREFIXES = new Set(['000', '999'])

/**
 * 365xx is mostly eastern Baldwin County (Gulf Shores, Orange Beach); 366xx is Mobile.
 * Our ZIP+3 table uses a Mobile centroid for 365 — override for jobsite eligibility.
 */
const JOBSITE_ZIP3_LONGITUDE_OVERRIDES: Record<string, number> = {
  '365': ORANGE_BEACH_AL_MERIDIAN_LNG,
}

function jobsiteLongitude(clean5: string): number {
  const zip3 = clean5.slice(0, 3)
  if (JOBSITE_ZIP3_LONGITUDE_OVERRIDES[zip3] !== undefined) {
    return JOBSITE_ZIP3_LONGITUDE_OVERRIDES[zip3]
  }
  return getZipCoordinates(clean5).lng
}

/** True when the proposed jobsite is not west of Orange Beach, AL (Full Service eligible). */
export function isJobsiteInBlissFullServiceArea(clean5: string): boolean {
  if (clean5.length !== 5) return false
  return jobsiteLongitude(clean5) >= ORANGE_BEACH_AL_MERIDIAN_LNG
}

/** @deprecated Use isJobsiteInBlissFullServiceArea */
export function isJobsiteInBlissServiceArea(clean5: string): boolean {
  return isJobsiteInBlissFullServiceArea(clean5)
}

export function blissJobsiteQualifiesForFullService(rawZip: string): boolean {
  const status = blissZipServiceStatus(rawZip)
  if (status === 'incomplete') return true
  return status === 'full_service'
}

export function blissJobsiteQualifiesForPaidDesignOffers(rawZip: string): boolean {
  const status = blissZipServiceStatus(rawZip)
  return status === 'full_service' || status === 'design_consult_only'
}

export function blissZipServiceStatus(rawZip: string): BlissZipServiceStatus {
  const clean = rawZip.replace(/\D/g, '').slice(0, 5)
  if (clean.length < 5) return 'incomplete'

  const prefix = clean.slice(0, 3)
  if (INVALID_ZIP_PREFIXES.has(prefix)) return 'invalid'

  if (isJobsiteInBlissFullServiceArea(clean)) return 'full_service'

  return 'design_consult_only'
}

/** Jobsite ZIP from a quote_requests row or normalized client object. */
export function blissJobsiteZipFromQuoteRecord(quote: {
  contact_info?: { zipCode?: string | null }
  client?: { zipCode?: string | null; zip?: string | null }
  project_context?: { blissDesignFunnel?: { projectZip?: string | null } }
}): string {
  const zip =
    quote.contact_info?.zipCode ||
    quote.client?.zipCode ||
    quote.client?.zip ||
    quote.project_context?.blissDesignFunnel?.projectZip ||
    ''
  return String(zip).replace(/\D/g, '').slice(0, 5)
}

/** One line for PDF / email spec tables — no mileage or shop reference. */
export function blissCustomerProjectLocationLine(projectZip: string | undefined | null): string | null {
  const zip = projectZip?.trim()
  if (!zip) return null
  if (blissZipServiceStatus(zip) === 'full_service') {
    return `Jobsite ZIP ${zip} (Full Service area)`
  }
  return `Jobsite ZIP ${zip}`
}
